import ApexCharts, { ApexOptions } from 'apexcharts';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '../../../components/Box';
import CustomDatePicker from '../../../components/CustomDatePicker';
import { Helpers } from '../../../helpers/Helpers';
import { DashboardCard, GetModuleDashboardResponse, GetModuleDashboardResponseData } from '../../../models/response/dashboard/GetModuleDashboardResponse';
import { storeActivityIndicator } from '../../../redux/reducers/authSlice';
import { RootState } from '../../../redux/store';
import { DashboardService } from '../../../services/DashboardService';
import { KTCard } from '../../../_metronic/helpers/components/KTCard';
import YurticiMutabakatKartlari from '../Components/YurticiMutabakatKartlari';

interface Props {
    module: string
}

// Kart rengi: sol seritte ve simgede kullanilir.
const tones: any = {
    primary: '#009ef7',
    success: '#50cd89',
    warning: '#f1bc00',
    danger: '#f1416c',
    info: '#7239ea',
    gray: '#a1a5b7'
}

// Karta tiklaninca acilacak ekranin menu adi: yetkisi olmayan kullanicida kart tiklanmaz.
const linkMenus: any = {
    '/yurticicargo/list': 'Kargo Modülü > Yurtiçi Kargo > Siparişler',
    '/ambar/list': 'Kargo Modülü > Nakliye > Siparişler',
    '/ambarvoyage/list': 'Kargo Modülü > Nakliye > Seferler',
    '/saleinvoice/list': 'Ön Muhasebe > Fiş / Faturalar > Satış Faturaları',
    '/purchaseinvoice/list': 'Ön Muhasebe > Fiş / Faturalar > Alış Faturaları'
}

const presets = [
    { key: 'today', label: 'Bugün', months: 0, days: 0 },
    { key: 'week', label: 'Son 7 gün', months: 0, days: 6 },
    { key: 'month', label: 'Son 1 ay', months: 1, days: 0 },
    { key: 'quarter', label: 'Son 3 ay', months: 3, days: 0 },
    { key: 'year', label: 'Bu yıl', months: 0, days: 0 }
]

const toInput = (date: Date) => date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);

const presetRange = (key: string) => {
    const end = new Date();
    const start = new Date();

    if (key == 'week') start.setDate(end.getDate() - 6);
    else if (key == 'month') start.setMonth(end.getMonth() - 1);
    else if (key == 'quarter') start.setMonth(end.getMonth() - 3);
    else if (key == 'year') { start.setMonth(0); start.setDate(1); }

    return { start: toInput(start), end: toInput(end) };
}

const ModuleDashboard = ({ module }: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth);

    const [data, setData] = useState<GetModuleDashboardResponseData | undefined>(undefined);
    const [preset, setPreset] = useState<string>('month');
    const [startDate, setStartDate] = useState<string | undefined>(presetRange('month').start);
    const [endDate, setEndDate] = useState<string | undefined>(presetRange('month').end);

    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<ApexCharts | undefined>(undefined);

    useEffect(() => {
        const range = presetRange('month');

        setPreset('month');
        setStartDate(range.start);
        setEndDate(range.end);
        getData(range.start, range.end, false);
    }, [module])

    useEffect(() => {
        if (!data || !chartRef.current)
            return;

        chartInstance.current?.destroy();
        chartInstance.current = new ApexCharts(chartRef.current, chartOptions(data));
        chartInstance.current.render();

        return () => { chartInstance.current?.destroy(); chartInstance.current = undefined; }
    }, [data])

    const chartOptions = (item: GetModuleDashboardResponseData): ApexOptions => ({
        chart: { type: 'bar', height: 300, toolbar: { show: false }, fontFamily: 'inherit' },
        series: [
            { name: item.chartFirstLabel, data: item.chart.map(x => x.first) },
            { name: item.chartSecondLabel, data: item.chart.map(x => x.second) }
        ],
        colors: ['#009ef7', '#50cd89'],
        plotOptions: { bar: { borderRadius: 5, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        legend: { show: true, position: 'bottom', horizontalAlign: 'left', markers: { radius: 6 } },
        grid: { borderColor: '#f1f1f4', strokeDashArray: 4 },
        xaxis: { categories: item.chart.map(x => x.label), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: '#a1a5b7', fontSize: '12px' } } },
        yaxis: {
            min: 0,
            // Deger kucukse eksen 1'er artar; boylece 3, 3, 2, 2 gibi tekrar eden basamak cikmaz.
            tickAmount: Math.min(5, Math.max(1, Math.max(...item.chart.map(x => Math.max(x.first, x.second)), 1))),
            forceNiceScale: true,
            labels: { style: { colors: '#a1a5b7', fontSize: '12px' }, formatter: (v: number) => Math.round(v).toString() }
        },
        tooltip: { y: { formatter: (v: number) => v.toLocaleString('tr-TR') } }
    })

    const getData = async (start: string | undefined, end: string | undefined, forceRefresh: boolean) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response: GetModuleDashboardResponse = await DashboardService.getModuleDashboard(module, start, end, forceRefresh);

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            setData(response.data);
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            dispatch(storeActivityIndicator(false))
        }
    }

    const selectPreset = (key: string) => {
        const range = presetRange(key);

        setPreset(key);
        setStartDate(range.start);
        setEndDate(range.end);
        getData(range.start, range.end, false);
    }

    const changeDate = (value: string | undefined, isStart: boolean) => {
        const start = isStart ? value : startDate;
        const end = isStart ? endDate : value;

        setPreset('');

        if (isStart) setStartDate(value); else setEndDate(value);

        if (!Helpers.isEmpty(start) && !Helpers.isEmpty(end))
            getData(start, end, false);
    }

    const openLink = (card: DashboardCard) => {
        if (Helpers.isEmpty(card.link))
            return;

        const menuName = linkMenus[card.link!];

        if (menuName && !auth.user.user?.data.authorizedMenus.find(x => x.menuName == menuName))
            return;

        // Liste, karttaki sayiyla ayni kumeyi gostersin diye filtre birlikte gonderilir.
        navigate(card.link!, { state: { columnFilters: card.filters ?? [] } });
    }

    // Kartlar satır numarasına göre gruplanır; bir satırda en fazla 5 kart olur, fazlası alt satıra iner.
    const renderRows = (cards: DashboardCard[]) => {
        const rows: DashboardCard[][] = [];

        Array.from(new Set(cards.map(x => x.row ?? 0))).sort((a, b) => a - b).forEach(rowNo => {
            const inRow = cards.filter(x => (x.row ?? 0) == rowNo);

            for (let i = 0; i < inRow.length; i += 5)
                rows.push(inRow.slice(i, i + 5));
        });

        return rows.map((row, index) => (
            <Box key={index} flexDirection='row' gap='16px' flexWrap='wrap' mb={index < rows.length - 1 ? '16px' : undefined}>
                {row.map(renderCard)}
            </Box>
        ));
    }

    const isClickable = (card: DashboardCard) => {
        if (Helpers.isEmpty(card.link))
            return false;

        const menuName = linkMenus[card.link!];

        return !menuName || auth.user.user?.data.authorizedMenus.find(x => x.menuName == menuName) != undefined;
    }

    // Küçük kart (E-İrsaliye / E-Fatura Durumları, 07.10.2026): 5 durum bir satıra sığsın.
    const renderCompactCard = (card: DashboardCard) => (
        <Box key={card.key} flex='1 1 100px' minWidth='100px'>
            <Box
                onClick={isClickable(card) ? () => openLink(card) : undefined}
                className={isClickable(card) ? 'actionIcon' : undefined}
                bg='#ffffff'
                p='10px 12px'
                borderRadius='8px'
                borderLeft={'4px solid ' + (tones[card.tone] ?? tones.primary)}
                boxShadow='0 2px 8px rgba(0, 0, 0, 0.05)'
                height='100%'
            >
                <div className='text-gray-600 mb-1' style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{card.label}</div>
                <h2 className='m-0 p-0' style={{ fontSize: 22, fontWeight: 700 }}>{card.value}</h2>
            </Box>
        </Box>
    )

    const renderCard = (card: DashboardCard) => (
        <Box key={card.key} flex='1 1 220px' minWidth='220px'>
            <Box
                onClick={isClickable(card) ? () => openLink(card) : undefined}
                className={isClickable(card) ? 'actionIcon' : undefined}
                bg='#ffffff'
                p='18px 20px'
                borderRadius='8px'
                borderLeft={'4px solid ' + (tones[card.tone] ?? tones.primary)}
                boxShadow='0 2px 8px rgba(0, 0, 0, 0.05)'
                height='100%'
            >
                <Box className='text-gray-600' mb='8px'>{card.label}</Box>

                <Box flexDirection='row' alignItems='center' gap='8px'>
                    <h1 className='m-0 p-0' style={{ fontSize: 30, fontWeight: 700 }}>{card.value}</h1>

                    {
                        card.changePercent != null && (
                            <span className={'badge ' + (card.changePercent >= 0 ? 'badge-light-success' : 'badge-light-danger')}>
                                {(card.changePercent > 0 ? '+' : '') + card.changePercent + '%'}
                            </span>
                        )
                    }
                </Box>

                {/* Açıklamadaki "\n" satır kırar (ör. Finans kartı: aralık + vadesi girilmemiş faturalar). */}
                {card.hint && <Box className='text-gray-500' mt='6px'>{card.hint.split('\n').map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}</Box>}
            </Box>
        </Box>
    )

    return (
        <React.Fragment>
            <KTCard>
                <Box p='12px 16px' flexDirection='row' alignItems='center' gap='10px' flexWrap='wrap'>
                    {
                        presets.map(item => (
                            <Box key={item.key} onClick={() => selectPreset(item.key)} className='actionIcon'>
                                <span className={'badge ' + (preset == item.key ? 'badge-light-primary' : 'badge-light')} style={{ padding: '10px 14px', fontSize: 13 }}>
                                    {item.label}
                                </span>
                            </Box>
                        ))
                    }

                    <Box width='170px' ml='8px'>
                        <CustomDatePicker value={startDate} setValue={(value: string | undefined) => changeDate(value, true)} />
                    </Box>

                    <Box className='text-gray-500'>—</Box>

                    <Box width='170px'>
                        <CustomDatePicker value={endDate} setValue={(value: string | undefined) => changeDate(value, false)} />
                    </Box>

                    <Box flexDirection='row' alignItems='center' gap='12px' ml='auto'>
                        {
                            data && (
                                <Box className='text-gray-500' textWrap='noWrap'>
                                    Veri {data.generatedAtStr} itibarıyla{data.fromCache ? ' (önbellek)' : ''}
                                </Box>
                            )
                        }

                        <button className='btn btn-sm btn-light-primary text-nowrap' onClick={() => getData(startDate, endDate, true)}>
                            <i className='fa fa-rotate-right me-1' /> Yenile
                        </button>
                    </Box>
                </Box>
            </KTCard>

            {
                data && (
                    <React.Fragment>
                        <Box mt='20px' mb='10px' flexDirection='row' alignItems='center' gap='10px'>
                            <h4 className='m-0 p-0'>Anlık durum</h4>
                            <Box className='text-gray-500'>Bugün itibarıyla; tarih aralığından etkilenmez</Box>
                        </Box>

                        {renderRows(data.snapshot)}

                        {/* Yurtiçi gider mutabakatı (24.10.2026, #485): yalnız yetkisi olana, kendi verisiyle. */}
                        {module == 'YURTICI_CARGO' && <YurticiMutabakatKartlari />}

                        <Box mt='24px' mb='10px' flexDirection='row' alignItems='center' gap='10px'>
                            <h4 className='m-0 p-0'>Seçilen dönem</h4>
                            <Box className='text-gray-500'>{data.startDateStr} – {data.endDateStr}</Box>
                        </Box>

                        {renderRows(data.period)}

                        {
                            (data.groups ?? []).map(group => (
                                <React.Fragment key={group.key}>
                                    <Box mt='24px' mb='10px' flexDirection='row' alignItems='center' gap='10px'>
                                        <h4 className='m-0 p-0'>{group.title}</h4>
                                        {group.hint && <Box className='text-gray-500'>{group.hint}</Box>}
                                    </Box>

                                    {
                                        group.rows.map((row, index) => (
                                            <React.Fragment key={index}>
                                                {/* Satır başlığı (opsiyonel, ör. Finans: Tahsilatlar / Ödemeler) */}
                                                {group.rowTitles?.[index] && <div className='text-gray-600 fw-semibold mb-2' style={{ fontSize: 13 }}>{group.rowTitles[index]}</div>}

                                                <Box flexDirection='row' gap={group.compact ? '10px' : '16px'} flexWrap='wrap' mb={index < group.rows.length - 1 ? (group.compact ? '10px' : '16px') : undefined}>
                                                    {row.map(group.compact ? renderCompactCard : renderCard)}
                                                </Box>
                                            </React.Fragment>
                                        ))
                                    }
                                </React.Fragment>
                            ))
                        }

                        <Box mt='24px' flexDirection='row' gap='16px' flexWrap='wrap'>
                            <Box flex={2} minWidth='420px'>
                                <KTCard>
                                    <Box p='18px 20px'>
                                        <h4 className='m-0 p-0'>{data.chartTitle}</h4>
                                        <Box className='text-gray-500' mt='4px'>Seçilen dönem, haftalık</Box>

                                        <Box mt='10px' _ref={chartRef}>{' '}</Box>
                                    </Box>
                                </KTCard>
                            </Box>

                            <Box flex={1} minWidth='300px'>
                                <KTCard>
                                    <Box p='18px 20px'>
                                        <h4 className='m-0 p-0'>{data.topListTitle}</h4>
                                        <Box className='text-gray-500' mt='4px' mb='16px'>{data.topListHint}</Box>

                                        {
                                            data.topList.length == 0 ?
                                                <Box className='text-gray-500'>Seçilen dönemde kayıt yok.</Box>
                                                :
                                                data.topList.map((item, index) => (
                                                    <Box key={index} mb='12px'>
                                                        <Box flexDirection='row' justifyContent='flex-start' gap='10px'>
                                                            <Box flex={1} textWrap='noWrap' overflow='hidden' title={item.name}>{item.name}</Box>
                                                            <Box className='fw-bold'>{item.value.toLocaleString('tr-TR')}</Box>
                                                        </Box>

                                                        <Box mt='4px' height='6px' borderRadius='3px' bg='#f1f1f4'>
                                                            <Box
                                                                height='6px'
                                                                borderRadius='3px'
                                                                bg={Object.values(tones)[index % 5] as string}
                                                                width={Math.max(4, Math.round(item.value * 100 / Math.max(...data.topList.map(x => x.value)))) + '%'}>{' '}</Box>
                                                        </Box>
                                                    </Box>
                                                ))
                                        }
                                    </Box>
                                </KTCard>
                            </Box>
                        </Box>
                    </React.Fragment>
                )
            }
        </React.Fragment>
    )
}

export default ModuleDashboard;
